import { Component, ElementRef, HostListener, Input, OnDestroy, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators
} from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute } from '@angular/router';
import { combineLatest, EMPTY, BehaviorSubject, Subject } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  finalize,
  map,
  switchMap,
  tap,
  takeUntil
} from 'rxjs/operators';
import {
  AspNetValidationProblemDetails,
  DataEntityVolumeApiResponse,
  DataEntityVolumeCertificate,
  DataEntityVolumeCertificateCapabilityProof,
  DataEntityVolumeCertificateComplianceAssessment,
  DataEntityVolumeCertificateFormula,
  DataEntityVolumeCertificateFormulaResult,
  DataEntityVolumeCertificateParameters,
  DataEntityVolumeCertificateConstants,
  DataEntityVolumeCertificateRegression,
  DataEntityVolumeCertificateMonteCarloSimulation,
  DataEntityVolumeCertificateDistribution,
  DataEntityVolumeFormModel,
  DataEntityVolumeLookupModel,
  DataEntityVolumeLookupResponse,
  DataEntityVolumeLookupSamplerModel,
  DataEntityVolumeSaveRequest,
  DataEntityVolumeSaveResponse,
  DistributionBaseDTO,
  DistributionType,
  FiniteNumberOrPositiveInfinity,
  LimitUncertaintyDTO,
  MeasurementModel,
  NModel,
  ObservationMethod,
  TypeAUncertaintyEstimates,
  TypeAUncertaintyEvaluations,
  ValueContainer
} from 'src/app/Interface/DataEntity/DataEntityVolumeForm_Interface';
import { AlertifyService } from 'src/app/Service/Alertify.service';
import { DataEntityVolumeFormService } from 'src/app/Service/DataEntity/DataEntityVolumeForm.service';
import defaultVolumeData from './data-entity-volume-form.defaults.json';
import {
  DISTRIBUTION_TYPES,
  dateOrderValidator,
  distributionTypeValidator,
  enumValueValidator,
  finiteNumberOrPositiveInfinityValidator,
  finiteNumberValidator,
  nonNegativeFiniteNumberValidator,
  normalizeDoF,
  orderedNumberValidator,
  positiveFiniteNumberValidator
} from './data-entity-volume-form.validators';

interface NamedField {
  key: string;
  label: string;
}

interface UncertaintySection {
  key: string;
  label: string;
  kind: 'limit' | 'expanded';
  allowInfiniteStandardUncertainty?: boolean;
}

type CertificateRegistrationStatus =
  | 'unknown'
  | 'registered'
  | 'notRegistered';

@Component({
  selector: 'app-data-entity-volume-form',
  templateUrl: './DataEntityVolumeForm.component.html',
  styleUrls: ['./DataEntityVolumeForm.component.scss']
})
export class DataEntityVolumeFormComponent implements OnInit, OnDestroy {
  form: FormGroup;
  isLoadingInitialData = false;
  isSubmitting = false;
  certificateRegistrationStatus: CertificateRegistrationStatus = 'unknown';
  lastSaveResponse: DataEntityVolumeSaveResponse | null = null;
  certificate: DataEntityVolumeCertificate | null = null;
  certificateFilePath: string | null = null;
  uncertaintyReportFilePath: string | null = null;
  isDownloadingCertificate = false;
  isDownloadingUncertaintyReport = false;
  expandedM2Rows: boolean[] = [];

  private _entityId: number | null | undefined;

  @Input()
  set entityId(value: number | null | undefined) {
    this._entityId = value;
    if (this.entityLoadingInitialized) {
      this.resolveAndLoadEntity();
    }
  }

  get entityId(): number | null | undefined {
    return this._entityId;
  }

  readonly typeAUncertaintyEvaluationOptions = [
    { value: TypeAUncertaintyEvaluations.Experimental, label: 'Experimental' },
    { value: TypeAUncertaintyEvaluations.PooledEstimate, label: 'Pooled Estimate' }
  ];
  readonly observationMethodOptions = [
    { value: ObservationMethod.Direct, label: 'Direct' },
    { value: ObservationMethod.Indirect, label: 'Indirect' }
  ];
  readonly typeAUncertaintyEstimateOptions = [
    { value: TypeAUncertaintyEstimates.Standard, label: 'Standard' },
    { value: TypeAUncertaintyEstimates.Bayesian, label: 'Bayesian' }
  ];
  readonly distributionTypes = DISTRIBUTION_TYPES;
  readonly distributionLabels: { [key: string]: string } = {
    Automatic: 'Automatic', Normal: 'Normal', Rectangular: 'Rectangular',
    Trapezoidal: 'Trapezoidal', Triangular: 'Triangular', StudentT: 'Student T'
  };
  readonly decisionRules = [
    { value: 0, label: '0 - Stringent Acceptance / Stringent Rejection' },
    { value: 1, label: '1 - Simple Acceptance / Simple Rejection' },
    { value: 2, label: '2 - Stringent Acceptance / Relaxed Rejection' },
    { value: 3, label: '3 - Relaxed Acceptance / Stringent Rejection' },
    { value: 4, label: '4 - Stringent Acceptance / Simple Rejection' },
    { value: 5, label: '5 - Simple Acceptance / Stringent Rejection' }
  ];
  readonly fieldHelp: { [key: string]: string } = {
    Z: 'is the combined factor for buoyancy correction and conversion from mass to volume',
    Y: 'is the thermal expansion correction factor of the delivering device',
    m2: 'is the balance reading of the weighing vessel after delivery of the measured volume of water',
    m0: 'is the balance reading of the weighing vessel before delivery of the measured volume of water',
    mE: 'is the balance reading of the mass loss due to evaporation of liquid during the measurement',
    DmuB: 'Uncertainty of Balance',
    DmLB: 'Linearity of Balance',
    DmRB1: 'reproducibility of Balance with empty vessel',
    DmRB2: 'reproducibility of Balance with filled vessel',
    DDmrB1: 'readability of Balance with empty vessel',
    DDmrB2: 'readability of Balance with filled vessel',
    DmTDB: 'temperature drift of balance',
    k0: 'Dimension Convert Factor',
    pb: 'is the density of the standard weight used to calibrate the balance',
    rhoA: 'is the density of air',
    rhoW: 'is the density of water',
    a0: 'is equal to 999.85308 kg/m³',
    a1: 'is equal to 6.32693 × 10⁻² °C⁻¹ kg/m³',
    tW: 'is the water temperature in degrees Celsius',
    a2: 'is equal to 8.523829 × 10⁻³ °C⁻² kg/m³',
    a3: 'is equal to 6.943248 × 10⁻⁵ °C⁻³ kg/m³',
    a4: 'is equal to 3.821216 × 10⁻⁷ °C⁻⁴ kg/m³',
    Pa_IND: 'is the pressure, expressed in hectopascals (hPa)',
    Qa_IND: 'is the relative humidity, expressed as a percentage',
    ta_IND: 'is the air temperature, expressed in degrees Celsius',
    aC: 'is the cubic expansion coefficient in °C⁻¹',
    td: 'is the device temperature in degrees Celsius',
    td20: 'is equal to 20 °C'
  };
  readonly fieldLabels = {
    Description: 'نام دستگاه',
    Expert: 'نام کارشناس',
    Manufacturer: 'شرکت سازنده',
    ModelDevice: 'مدل دستگاه',
    TypeDevice: 'نوع دستگاه',
    CodeNoDevice: 'کد دستگاه',
    SerialNoDevice: 'شماره سریال دستگاه',
    CustomerName: 'نام مشتری',
    CertificateAddress: 'آدرس درج‌شده در گواهی',
    LabAdderss: 'آدرس آزمایشگاه',
    IssueDateMiladi: 'تاریخ صدور میلادی',
    CalibrationDateMiladi: 'تاریخ کالیبراسیون میلادی',
    RecalibrationDateMiladi: 'تاریخ کالیبراسیون مجدد میلادی'
  };
  readonly valueContainerFields: NamedField[] = [
    { key: 'm0', label: 'm0' },
    { key: 'k0', label: 'k0' },
    { key: 'pb', label: 'pb' },
    { key: 'a0', label: 'a0' },
    { key: 'a1', label: 'a1' },
    { key: 'a2', label: 'a2' },
    { key: 'a3', label: 'a3' },
    { key: 'a4', label: 'a4' },
    { key: 'aC', label: 'aC' },
    { key: 'td20', label: 'td20' }
  ];
  readonly uncertaintySections: UncertaintySection[] = [
    { key: 'mE', label: 'mE', kind: 'limit' },
    { key: 'DmuB', label: 'DmuB', kind: 'expanded' },
    { key: 'DmLB', label: 'DmLB', kind: 'limit' },
    { key: 'DmRB1', label: 'DmRB1', kind: 'limit' },
    { key: 'DmRB2', label: 'DmRB2', kind: 'limit' },
    { key: 'DDmrB1', label: 'DDmrB1', kind: 'limit' },
    {
      key: 'DDmrB2',
      label: 'DDmrB2',
      kind: 'limit',
      allowInfiniteStandardUncertainty: true
    },
    { key: 'DmTDB', label: 'DmTDB', kind: 'limit' },
    { key: 'Pa_IND', label: 'Pa_IND', kind: 'limit' },
    { key: 'Qa_IND', label: 'Qa_IND', kind: 'limit' },
    { key: 'ta_IND', label: 'ta_IND', kind: 'limit' },
    { key: 'tW', label: 'tW', kind: 'limit' },
    { key: 'td', label: 'td', kind: 'limit' }
  ];

  private readonly destroy$ = new Subject<void>();
  private readonly formBindings$ = new Subject<void>();
  private readonly resolvedEntityId$ = new BehaviorSubject<number | null>(null);
  private entityLoadingInitialized = false;
  private initialFormSnapshot: DataEntityVolumeFormModel;
  private routeEntityId: number | null = null;
  private preserveRegisteredNModels = false;

  constructor(
    private formBuilder: FormBuilder,
    private readonly dataEntityVolumeFormService: DataEntityVolumeFormService,
    private alertifyService: AlertifyService,
    private readonly route: ActivatedRoute,
    private hostElement: ElementRef
  ) { }

  ngOnInit(): void {
    this.initializeEntityLoading();
    this.initializeRouteEntityId();
    this.entityLoadingInitialized = true;
    this.resolveAndLoadEntity();
  }

  ngOnDestroy(): void {
    this.formBindings$.next();
    this.formBindings$.complete();
    this.resolvedEntityId$.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }

  @HostListener('wheel', ['$event'])
  onNumberInputWheel(event: WheelEvent): void {
    const target = event.target as HTMLInputElement | null;
    if (target && target.tagName === 'INPUT' && target.type === 'number' && document.activeElement === target) {
      target.blur();
    }
  }

  get certificateRegistrationMessage(): string | null {
    switch (this.certificateRegistrationStatus) {
      case 'registered':
        return 'ثبت گواهی انجام شده است';
      case 'notRegistered':
        return 'برای این دستگاه، ثبت گواهی انجام نشده است';
      default:
        return null;
    }
  }

  isValidEntityId(id: unknown): id is number {
    return this.parsePositiveEntityId(id) !== null;
  }

  private initializeEntityLoading(): void {
    this.resolvedEntityId$
      .pipe(
        distinctUntilChanged(),
        tap(() => {
          this.clearCertificateUiState();
          this.certificateRegistrationStatus = 'unknown';
        }),
        switchMap(id => {
          if (!this.isValidEntityId(id)) {
            this.initializeCreateMode();
            return EMPTY;
          }

          this.isLoadingInitialData = true;

          return this.dataEntityVolumeFormService.getById(id).pipe(
            finalize(() => {
              this.isLoadingInitialData = false;
            }),
            catchError((error: HttpErrorResponse) => {
              this.handleLookupError(error);
              return EMPTY;
            })
          );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe(rawResponse =>
        this.handleDataEntityVolumeResponse(rawResponse, 'get')
      );
  }

  private initializeRouteEntityId(): void {
    combineLatest(this.route.pathFromRoot.map(route => route.paramMap))
      .pipe(
        map(paramMaps => {
          for (let index = paramMaps.length - 1; index >= 0; index -= 1) {
            const routeId = this.parsePositiveEntityId(paramMaps[index].get('id'));
            if (routeId !== null) {
              return routeId;
            }
          }

          return null;
        }),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe(routeId => {
        this.routeEntityId = routeId;

        if (this.entityLoadingInitialized) {
          this.resolveAndLoadEntity();
        }
      });
  }

  private resolveAndLoadEntity(): void {
    this.loadById(this.resolveEffectiveEntityId());
  }

  private loadById(id: number | null): void {
    this.resolvedEntityId$.next(id);
  }

  private parsePositiveEntityId(value: unknown): number | null {
    if (typeof value === 'number') {
      return Number.isSafeInteger(value) && value > 0 ? value : null;
    }

    if (typeof value !== 'string') {
      return null;
    }

    const normalized = value.trim();
    if (normalized.length === 0) {
      return null;
    }

    const numericValue = Number(normalized);
    return Number.isSafeInteger(numericValue) && numericValue > 0
      ? numericValue
      : null;
  }

  private resolveEffectiveEntityId(): number | null {
    const inputId = this.parsePositiveEntityId(this._entityId);
    if (inputId !== null) {
      return inputId;
    }

    return this.routeEntityId;
  }

  private initializeCreateMode(): void {
    this.isLoadingInitialData = false;
    this.certificateRegistrationStatus = 'unknown';
    this.clearCertificateUiState();
    const defaults = this.cloneDefaultData();
    this.initialFormSnapshot = this.cloneFormModel(defaults);
    this.initializeForm(defaults);
  }

  extractSamplerModel(
    response: DataEntityVolumeLookupResponse | null
  ): DataEntityVolumeLookupSamplerModel | null {
    return response && response.samplerModel ? response.samplerModel : null;
  }

  extractLookupModel(
    response: DataEntityVolumeLookupResponse | null
  ): DataEntityVolumeLookupModel | null {
    const samplerModel = this.extractSamplerModel(response);
    return samplerModel
      ? (this.mapSamplerModelToFormLookup(samplerModel) as DataEntityVolumeLookupModel)
      : null;
  }

  isEmptyLookupModel(model: unknown): boolean {
    const record = this.asRecord(model);
    if (!record) {
      return true;
    }

    return !this.hasLookupFields(record);
  }

  mapLookupResponseToFormModel(
    model: DataEntityVolumeLookupModel
  ): DataEntityVolumeFormModel {
    return this.mergeWithDefaults(this.cloneDefaultData(), model);
  }

  mapRegisteredSamplerModelToFormModel(
    samplerModel: DataEntityVolumeLookupSamplerModel
  ): DataEntityVolumeFormModel {
    const registeredLookup = this.mapSamplerModelToFormLookup(samplerModel);
    const mapped = this.mergeWithDefaults(
      this.createNeutralFormModel(),
      registeredLookup
    );
    const registeredNModels = Array.isArray(registeredLookup.NModels)
      ? registeredLookup.NModels
      : [];
    mapped.NModels = registeredNModels.map(item =>
      this.normalizeLoadedNModel(item, null, this.createDefaultNModel(), true)
    );
    return mapped;
  }

  private createNeutralFormModel(): DataEntityVolumeFormModel {
    const neutral = this.cloneDefaultData();
    Object.keys(neutral).forEach(key => {
      const value = (neutral as any)[key];
      if (typeof value === 'string') { (neutral as any)[key] = ''; }
      else if (typeof value === 'number') { (neutral as any)[key] = 0; }
      else if (typeof value === 'boolean') { (neutral as any)[key] = false; }
    });
    neutral.m2 = [];
    neutral.NModels = [];
    neutral.Unit = '';
    neutral.UncertaintyFloat = 2;
    this.valueContainerFields.forEach(field => neutral[field.key].Value = 0);
    this.uncertaintySections.forEach(section => {
      const item = neutral[section.key] as any;
      Object.keys(item).forEach(key => {
        if (key === 'Distribution') {
          item[key] = { $type: 'Normal', CoverageFactor: 1, ShapeFactor: 1 };
        } else {
          item[key] = 0;
        }
      });
    });
    return neutral;
  }

  private mapSamplerModelToFormLookup(
    samplerModel: DataEntityVolumeLookupSamplerModel
  ): DataEntityVolumeLookupModel {
    return {
      Unit: samplerModel.unit,
      UncertaintyFloat: samplerModel.uncertaintyFloat,
      m0: this.mapApiValueContainer(samplerModel.m0),
      k0: this.mapApiValueContainer(samplerModel.k0),
      pb: this.mapApiValueContainer(samplerModel.pb),
      a0: this.mapApiValueContainer(samplerModel.a0),
      a1: this.mapApiValueContainer(samplerModel.a1),
      a2: this.mapApiValueContainer(samplerModel.a2),
      a3: this.mapApiValueContainer(samplerModel.a3),
      a4: this.mapApiValueContainer(samplerModel.a4),
      aC: this.mapApiValueContainer(samplerModel.aC),
      td20: this.mapApiValueContainer(samplerModel.td20),
      m2: (samplerModel.m2 || []).map(item => this.mapApiMeasurement(item)),
      mE: this.mapApiLimitUncertainty(samplerModel.mE),
      DmuB: this.mapApiExpandedUncertainty(samplerModel.DmuB),
      DmLB: this.mapApiLimitUncertainty(samplerModel.DmLB),
      DmRB1: this.mapApiLimitUncertainty(samplerModel.DmRB1),
      DmRB2: this.mapApiLimitUncertainty(samplerModel.DmRB2),
      DDmrB1: this.mapApiLimitUncertainty(samplerModel.DDmrB1),
      DDmrB2: this.mapApiLimitUncertainty(samplerModel.DDmrB2),
      DmTDB: this.mapApiLimitUncertainty(samplerModel.DmTDB),
      Pa_IND: this.mapApiLimitUncertainty(samplerModel.Pa_IND),
      Qa_IND: this.mapApiLimitUncertainty(samplerModel.Qa_IND),
      ta_IND: this.mapApiLimitUncertainty(samplerModel.ta_IND),
      tW: this.mapApiLimitUncertainty(samplerModel.tW),
      td: this.mapApiLimitUncertainty(samplerModel.td),
      Description: samplerModel.description,
      Expert: samplerModel.expert,
      IssueDateMiladi: samplerModel.issueDateMiladi,
      CalibrationDateMiladi: samplerModel.calibrationDateMiladi,
      RecalibrationDateMiladi: samplerModel.recalibrationDateMiladi,
      Manufacturer: samplerModel.manufacturer,
      ModelDevice: samplerModel.modelDevice,
      TypeDevice: samplerModel.typeDevice,
      CodeNoDevice: samplerModel.codeNoDevice,
      SerialNoDevice: samplerModel.serialNoDevice,
      CustomerName: samplerModel.customerName,
      CertificateAddress: samplerModel.certificateAddress,
      LabAdderss: samplerModel.labAdderss,
      Farsi: samplerModel.farsi,
      Value: samplerModel.value,
      From: samplerModel.from,
      To: samplerModel.to,
      Division: samplerModel.division,
      LetterId: samplerModel.letterId,
      CalibrationDetailId: samplerModel.calibrationDetailId,
      NModels: (samplerModel.nModels || []).map(item => this.mapApiNModel(item))
    };
  }

  private apiProperty(source: unknown, camelCase: string, pascalCase: string): unknown {
    const record = this.asRecord(source) || {};
    return record[camelCase] !== undefined ? record[camelCase] : record[pascalCase];
  }

  private mapApiValueContainer(source: unknown): ValueContainer | null {
    if (!this.asRecord(source)) { return null; }
    return { Value: this.apiProperty(source, 'value', 'Value') as number };
  }

  private mapApiDistribution(source: unknown): DistributionBaseDTO | null {
    if (!this.asRecord(source)) { return null; }
    return {
      $type: this.apiProperty(source, '$type', '$type') as DistributionType,
      CoverageFactor: this.apiProperty(source, 'coverageFactor', 'CoverageFactor') as number,
      ShapeFactor: this.apiProperty(source, 'shapeFactor', 'ShapeFactor') as number
    };
  }

  private mapApiMeasurement(source: unknown): MeasurementModel {
    const observations = this.apiProperty(source, 'observations', 'Observations');
    const indirect = this.apiProperty(source, 'indirectObservations', 'IndirectObservations');
    return {
      UncertaintyEvaluation: this.apiProperty(source, 'uncertaintyEvaluation', 'UncertaintyEvaluation') as TypeAUncertaintyEvaluations,
      StandardDeviation: this.apiProperty(source, 'standardDeviation', 'StandardDeviation') as number,
      Observations: Array.isArray(observations) ? [...observations] as number[] : [],
      IndirectObservations: Array.isArray(indirect) ? [...indirect] as number[] : [],
      ObservationMethod: this.apiProperty(source, 'observationMethod', 'ObservationMethod') as ObservationMethod,
      UncertaintyEstimate: this.apiProperty(source, 'uncertaintyEstimate', 'UncertaintyEstimate') as TypeAUncertaintyEstimates,
      DoF: this.apiProperty(source, 'doF', 'DoF') as FiniteNumberOrPositiveInfinity,
      Value: this.apiProperty(source, 'value', 'Value') as number,
      StandardUncertainty: this.apiProperty(source, 'standardUncertainty', 'StandardUncertainty') as number,
      SensitivityCoeff: this.apiProperty(source, 'sensitivityCoeff', 'SensitivityCoeff') as number,
      UncertaintyContribution: this.apiProperty(source, 'uncertaintyContribution', 'UncertaintyContribution') as number,
      RelativeContribution: this.apiProperty(source, 'relativeContribution', 'RelativeContribution') as number
    };
  }

  private mapApiNModel(source: unknown): NModel {
    return {
      Value: this.apiProperty(source, 'value', 'Value') as string,
      Tolerance: this.apiProperty(source, 'tolerance', 'Tolerance') as number,
      TUR: this.apiProperty(source, 'tur', 'TUR') as number,
      LimitValue: this.apiProperty(source, 'limitValue', 'LimitValue') as number,
      Limit: this.apiProperty(source, 'limit', 'Limit') as number,
      LowerLimit: this.apiProperty(source, 'lowerLimit', 'LowerLimit') as number,
      UpperLimit: this.apiProperty(source, 'upperLimit', 'UpperLimit') as number,
      DecisionRule: this.apiProperty(source, 'decisionRule', 'DecisionRule') as number,
      Distribution: this.mapApiDistribution(this.apiProperty(source, 'distribution', 'Distribution')) as DistributionBaseDTO
    };
  }

  private mapApiUncertaintyBase(source: unknown): any {
    if (!this.asRecord(source)) { return null; }
    return {
      Distribution: this.mapApiDistribution(this.apiProperty(source, 'distribution', 'Distribution')),
      DoF: this.apiProperty(source, 'doF', 'DoF'),
      Value: this.apiProperty(source, 'value', 'Value'),
      StandardUncertainty: this.apiProperty(source, 'standardUncertainty', 'StandardUncertainty'),
      SensitivityCoeff: this.apiProperty(source, 'sensitivityCoeff', 'SensitivityCoeff'),
      UncertaintyContribution: this.apiProperty(source, 'uncertaintyContribution', 'UncertaintyContribution'),
      RelativeContribution: this.apiProperty(source, 'relativeContribution', 'RelativeContribution')
    };
  }

  private mapApiLimitUncertainty(source: unknown): LimitUncertaintyDTO | null {
    const mapped = this.mapApiUncertaintyBase(source);
    return mapped && {
      ...mapped,
      HalfWidthOfLimit: this.apiProperty(source, 'halfWidthOfLimit', 'HalfWidthOfLimit'),
      RelativeErrorOfUncertainty: this.apiProperty(source, 'relativeErrorOfUncertainty', 'RelativeErrorOfUncertainty')
    } as LimitUncertaintyDTO;
  }

  private mapApiExpandedUncertainty(source: unknown): DataEntityVolumeFormModel['DmuB'] | null {
    const mapped = this.mapApiUncertaintyBase(source);
    return mapped && {
      ...mapped,
      ExpandedUncertainty: this.apiProperty(source, 'expandedUncertainty', 'ExpandedUncertainty'),
      CoverageFactor: this.apiProperty(source, 'coverageFactor', 'CoverageFactor')
    } as DataEntityVolumeFormModel['DmuB'];
  }

  private hasCertificateFile(value: unknown): boolean {
    return typeof value === 'string' && value.trim().length > 0;
  }

  private mapUnregisteredCertificateDefaults(
    samplerModel: DataEntityVolumeLookupSamplerModel
  ): DataEntityVolumeFormModel {
    const defaults = this.cloneDefaultData();

    return {
      ...defaults,
      LetterId: samplerModel.letterId ?? defaults.LetterId,
      CalibrationDateMiladi:
        samplerModel.calibrationDateMiladi ?? defaults.CalibrationDateMiladi,
      IssueDateMiladi: samplerModel.issueDateMiladi ?? defaults.IssueDateMiladi,
      CodeNoDevice: samplerModel.codeNoDevice ?? defaults.CodeNoDevice,
      SerialNoDevice: samplerModel.serialNoDevice ?? defaults.SerialNoDevice,
      CustomerName: samplerModel.customerName ?? defaults.CustomerName,
      CertificateAddress:
        samplerModel.certificateAddress ?? defaults.CertificateAddress,
      CalibrationDetailId: this.resolveCalibrationDetailId(samplerModel)
    };
  }

  private resolveCalibrationDetailId(
    samplerModel: DataEntityVolumeLookupSamplerModel
  ): number {
    const resolvedValue = samplerModel.calibrationDetailId;

    return typeof resolvedValue === 'number' && Number.isFinite(resolvedValue)
      ? resolvedValue
      : this.cloneDefaultData().CalibrationDetailId;
  }

  mergeWithDefaults(
    defaults: DataEntityVolumeFormModel,
    loaded: DataEntityVolumeLookupModel
  ): DataEntityVolumeFormModel {
    const source = this.asRecord(loaded) || {};
    const loadedMeasurements = Array.isArray(source.m2)
      ? source.m2
      : null;
    const measurements = loadedMeasurements === null
      ? defaults.m2.map(item => this.clone(item))
      : loadedMeasurements.map((item, index) =>
        this.normalizeLoadedMeasurement(
          item,
          defaults.m2[index] || this.createDefaultMeasurement()
        )
      );
    const loadedNModels = Array.isArray(source.NModels)
      ? source.NModels
      : [];
    const nModels = measurements.map((measurement, index) =>
      this.normalizeLoadedNModel(
        loadedNModels[index],
        measurement.Value,
        defaults.NModels[index] || this.createDefaultNModel()
      )
    );

    return {
      Unit: this.normalizeString(source.Unit, defaults.Unit ?? ''),
      UncertaintyFloat: this.normalizeFiniteNumber(
        source.UncertaintyFloat,
        defaults.UncertaintyFloat ?? 2
      ),
      m0: this.normalizeValueContainer(source.m0, defaults.m0),
      k0: this.normalizeValueContainer(source.k0, defaults.k0),
      pb: this.normalizeValueContainer(source.pb, defaults.pb),
      a0: this.normalizeValueContainer(source.a0, defaults.a0),
      a1: this.normalizeValueContainer(source.a1, defaults.a1),
      a2: this.normalizeValueContainer(source.a2, defaults.a2),
      a3: this.normalizeValueContainer(source.a3, defaults.a3),
      a4: this.normalizeValueContainer(source.a4, defaults.a4),
      aC: this.normalizeValueContainer(source.aC, defaults.aC),
      td20: this.normalizeValueContainer(source.td20, defaults.td20),
      m2: measurements,
      mE: this.normalizeLoadedLimit(source.mE, defaults.mE),
      DmuB: this.normalizeLoadedExpanded(source.DmuB, defaults.DmuB),
      DmLB: this.normalizeLoadedLimit(source.DmLB, defaults.DmLB),
      DmRB1: this.normalizeLoadedLimit(source.DmRB1, defaults.DmRB1),
      DmRB2: this.normalizeLoadedLimit(source.DmRB2, defaults.DmRB2),
      DDmrB1: this.normalizeLoadedLimit(source.DDmrB1, defaults.DDmrB1),
      DDmrB2: this.normalizeLoadedLimit(
        source.DDmrB2,
        defaults.DDmrB2,
        true
      ),
      DmTDB: this.normalizeLoadedLimit(source.DmTDB, defaults.DmTDB),
      Pa_IND: this.normalizeLoadedLimit(source.Pa_IND, defaults.Pa_IND),
      Qa_IND: this.normalizeLoadedLimit(source.Qa_IND, defaults.Qa_IND),
      ta_IND: this.normalizeLoadedLimit(source.ta_IND, defaults.ta_IND),
      tW: this.normalizeLoadedLimit(source.tW, defaults.tW),
      td: this.normalizeLoadedLimit(source.td, defaults.td),
      Description: this.normalizeString(source.Description, defaults.Description),
      Expert: this.normalizeString(source.Expert, defaults.Expert),
      IssueDateMiladi: this.normalizeString(
        source.IssueDateMiladi,
        defaults.IssueDateMiladi
      ),
      CalibrationDateMiladi: this.normalizeString(
        source.CalibrationDateMiladi,
        defaults.CalibrationDateMiladi
      ),
      RecalibrationDateMiladi: this.normalizeString(
        source.RecalibrationDateMiladi,
        defaults.RecalibrationDateMiladi
      ),
      Manufacturer: this.normalizeString(source.Manufacturer, defaults.Manufacturer),
      ModelDevice: this.normalizeString(source.ModelDevice, defaults.ModelDevice),
      TypeDevice: this.normalizeString(source.TypeDevice, defaults.TypeDevice),
      CodeNoDevice: this.normalizeString(source.CodeNoDevice, defaults.CodeNoDevice),
      SerialNoDevice: this.normalizeString(
        source.SerialNoDevice,
        defaults.SerialNoDevice
      ),
      CustomerName: this.normalizeString(source.CustomerName, defaults.CustomerName),
      CertificateAddress: this.normalizeString(
        source.CertificateAddress,
        defaults.CertificateAddress
      ),
      LabAdderss: this.normalizeString(source.LabAdderss, defaults.LabAdderss),
      Farsi: this.normalizeBoolean(source.Farsi, defaults.Farsi),
      Value: this.normalizeFiniteNumber(source.Value, defaults.Value),
      From: this.normalizeFiniteNumber(source.From, defaults.From),
      To: this.normalizeFiniteNumber(source.To, defaults.To),
      Division: this.normalizePositiveNumber(source.Division, defaults.Division),
      LetterId: this.normalizeNonNegativeNumber(
        source.LetterId,
        defaults.LetterId
      ),
      CalibrationDetailId: this.normalizeNonNegativeNumber(
        source.CalibrationDetailId,
        defaults.CalibrationDetailId
      ),
      NModels: nModels
    };
  }

  normalizeLoadedMeasurement(
    value: unknown,
    fallback: MeasurementModel
  ): MeasurementModel {
    const source = this.asRecord(value) || {};
    return {
      UncertaintyEvaluation: this.normalizeEnum(
        source.UncertaintyEvaluation,
        [
          TypeAUncertaintyEvaluations.Experimental,
          TypeAUncertaintyEvaluations.PooledEstimate
        ],
        fallback.UncertaintyEvaluation
      ),
      StandardDeviation: this.normalizeNonNegativeNumber(
        source.StandardDeviation,
        fallback.StandardDeviation
      ),
      Observations: this.normalizeNumberArray(
        source.Observations,
        fallback.Observations
      ),
      IndirectObservations: this.normalizeNumberArray(
        source.IndirectObservations,
        fallback.IndirectObservations
      ),
      ObservationMethod: this.normalizeEnum(
        source.ObservationMethod,
        [ObservationMethod.Direct, ObservationMethod.Indirect],
        fallback.ObservationMethod
      ),
      UncertaintyEstimate: this.normalizeEnum(
        source.UncertaintyEstimate,
        [
          TypeAUncertaintyEstimates.Standard,
          TypeAUncertaintyEstimates.Bayesian
        ],
        fallback.UncertaintyEstimate
      ),
      DoF: this.normalizePositiveInfinity(source.DoF, fallback.DoF),
      Value: this.normalizeFiniteNumber(source.Value, fallback.Value),
      StandardUncertainty: this.normalizeNonNegativeNumber(
        source.StandardUncertainty,
        fallback.StandardUncertainty
      ),
      SensitivityCoeff: this.normalizeFiniteNumber(
        source.SensitivityCoeff,
        fallback.SensitivityCoeff
      ),
      UncertaintyContribution: this.normalizeFiniteNumber(
        source.UncertaintyContribution,
        fallback.UncertaintyContribution
      ),
      RelativeContribution: this.normalizeFiniteNumber(
        source.RelativeContribution,
        fallback.RelativeContribution
      )
    };
  }

  normalizeLoadedNModel(
    value: unknown,
    measurementValue: number | null,
    fallback: NModel,
    preserveValue = false
  ): NModel {
    const source = this.asRecord(value) || {};
    return {
      Value: preserveValue
        ? this.normalizeString(source.Value, '')
        : String(measurementValue),
      Tolerance: this.normalizeNonNegativeNumber(
        source.Tolerance,
        fallback.Tolerance
      ),
      TUR: this.normalizeNonNegativeNumber(source.TUR, fallback.TUR ?? 3),
      LimitValue: this.normalizeNonNegativeNumber(
        source.LimitValue,
        fallback.LimitValue ?? 3
      ),
      Limit: this.normalizeNonNegativeNumber(source.Limit, fallback.Limit),
      LowerLimit: this.normalizeFiniteNumber(
        source.LowerLimit,
        fallback.LowerLimit
      ),
      UpperLimit: this.normalizeFiniteNumber(
        source.UpperLimit,
        fallback.UpperLimit
      ),
      DecisionRule: this.normalizeNonNegativeNumber(
        source.DecisionRule,
        fallback.DecisionRule
      ),
      Distribution: this.normalizeDistribution(
        source.Distribution,
        fallback.Distribution
      )
    };
  }

  private unwrapDataEntityVolumeResponse(
    response: unknown
  ): DataEntityVolumeApiResponse | null {
    const source = this.asRecord(response);
    if (!source) {
      return null;
    }

    const candidates: unknown[] = [
      source,
      source.body,
      source.data,
      source.result,
      source.value
    ];

    for (const candidate of candidates) {
      const record = this.asRecord(candidate);
      if (record && ('samplerModel' in record || 'certificate' in record)) {
        return record as unknown as DataEntityVolumeApiResponse;
      }
    }

    return null;
  }

  private handleDataEntityVolumeResponse(
    rawResponse: unknown,
    source: 'get' | 'post'
  ): void {
    const response = this.unwrapDataEntityVolumeResponse(rawResponse);

    const samplerModel = this.extractSamplerModel(response);
    this.setCertificateUiState(
      response?.certificate ?? null,
      samplerModel?.certificatefile ?? null,
      samplerModel?.uncertaintyReportFile ?? null
    );

    if (source === 'get') {
      this.handleSamplerModelForForm(samplerModel);
      return;
    }

    this.updateCertificateRegistrationStatus(samplerModel);
  }

  private handleSamplerModelForForm(
    samplerModel: DataEntityVolumeLookupSamplerModel | null
  ): void {
    if (!samplerModel) {
      this.certificateRegistrationStatus = 'notRegistered';
      const defaults = this.cloneDefaultData();
      this.initialFormSnapshot = this.cloneFormModel(defaults);
      this.initializeForm(defaults);
      return;
    }

    if (!this.hasCertificateFile(samplerModel.certificatefile)) {
      this.certificateRegistrationStatus = 'notRegistered';
      const mappedDefaults =
        this.mapUnregisteredCertificateDefaults(samplerModel);
      this.initialFormSnapshot = this.cloneFormModel(mappedDefaults);
      this.initializeForm(mappedDefaults);
      this.patchAuthoritativeSamplerFields(samplerModel);
      return;
    }

    this.certificateRegistrationStatus = 'registered';
    const mappedRegisteredData =
      this.mapRegisteredSamplerModelToFormModel(samplerModel);
    this.initialFormSnapshot = this.cloneFormModel(mappedRegisteredData);
    this.initializeForm(mappedRegisteredData, true);
    this.patchAuthoritativeSamplerFields(samplerModel);
  }

  private patchAuthoritativeSamplerFields(
    samplerModel: DataEntityVolumeLookupSamplerModel
  ): void {
    this.form.patchValue(
      {
        LetterId: samplerModel.letterId,
        CalibrationDateMiladi: samplerModel.calibrationDateMiladi,
        IssueDateMiladi: samplerModel.issueDateMiladi,
        CodeNoDevice: samplerModel.codeNoDevice,
        SerialNoDevice: samplerModel.serialNoDevice,
        CustomerName: samplerModel.customerName,
        CertificateAddress: samplerModel.certificateAddress,
        CalibrationDetailId: samplerModel.calibrationDetailId,
        RecalibrationDateMiladi: samplerModel.recalibrationDateMiladi,
        Description: samplerModel.description,
        Manufacturer: samplerModel.manufacturer,
        ModelDevice: samplerModel.modelDevice,
        TypeDevice: samplerModel.typeDevice
      },
      { emitEvent: false }
    );
    this.initialFormSnapshot = this.cloneFormModel(
      this.form.getRawValue() as DataEntityVolumeFormModel
    );
  }

  private updateCertificateRegistrationStatus(
    samplerModel: DataEntityVolumeLookupSamplerModel | null
  ): void {
    this.certificateRegistrationStatus =
      samplerModel && this.hasCertificateFile(samplerModel.certificatefile)
        ? 'registered'
        : 'notRegistered';
  }

  private setCertificateUiState(
    certificate: unknown,
    certificateFilePath: unknown,
    uncertaintyReportFilePath: unknown
  ): void {
    this.certificate = this.normalizeCertificate(certificate);
    this.certificateFilePath = this.normalizeCertificateFilePath(
      certificateFilePath
    );
    this.uncertaintyReportFilePath = this.normalizeCertificateFilePath(
      uncertaintyReportFilePath
    );
  }

  private clearCertificateUiState(): void {
    this.certificate = null;
    this.certificateFilePath = null;
    this.uncertaintyReportFilePath = null;
  }

  private normalizeCertificate(
    certificate: unknown
  ): DataEntityVolumeCertificate | null {
    let normalizedCertificate = certificate;
    if (typeof certificate === 'string') {
      try {
        normalizedCertificate = JSON.parse(certificate) as unknown;
      } catch {
        return null;
      }
    }

    const source = this.asRecord(normalizedCertificate);
    if (!source) {
      return null;
    }

    return {
      name: this.normalizeDisplayText(source.name),
      description: this.normalizeDisplayText(source.description),
      formulas: this.normalizeCertificateFormulas(source.formulas),
      regression: this.normalizeCertificateRegression(source.regression)
    };
  }

  private normalizeCertificateFormulas(
    value: unknown
  ): DataEntityVolumeCertificateFormula[] {
    if (!Array.isArray(value)) {
      return [];
    }

    return value
      .map(item => this.normalizeCertificateFormula(item))
      .filter(
        (item): item is DataEntityVolumeCertificateFormula => item !== null
      );
  }

  private normalizeCertificateFormula(
    value: unknown
  ): DataEntityVolumeCertificateFormula | null {
    const source = this.asRecord(value);
    if (!source) {
      return null;
    }

    return {
      id: this.normalizeCertificateFiniteNumber(source.id),
      name: this.normalizeDisplayText(source.name),
      value: this.normalizeFormulaExpression(source.value),
      subValues: this.normalizeStringArray(source.subValues),
      result: this.normalizeCertificateFormulaResult(source.result),
      parameters: this.normalizeCertificateParameters(source.parameters),
      constants: this.normalizeCertificateConstants(source.constants),
      regression: this.normalizeCertificateRegression(source.regression),
      monteCarloSimulation: this.normalizeCertificateMonteCarloSimulation(
        source.monteCarloSimulation
      )
    };
  }

  private normalizeCertificateFormulaResult(
    value: unknown
  ): DataEntityVolumeCertificateFormulaResult | null {
    const source = this.asRecord(value);
    if (!source) {
      return null;
    }

    return {
      distribution: this.normalizeCertificateDistribution(source.distribution),
      capabilityProof: this.normalizeCapabilityProof(source.capabilityProof),
      complianceAssessment: this.normalizeComplianceAssessment(
        source.complianceAssessment
      ),
      doF: this.normalizeCertificateValue(source.doF),
      value: this.normalizeCertificateValue(source.value),
      standardUncertainty: this.normalizeCertificateValue(
        source.standardUncertainty
      ),
      expandedUncertainty: this.normalizeCertificateValue(
        source.expandedUncertainty
      ),
      resultDistribution: this.normalizeResultDistribution(
        source.resultDistribution
      ),
      name: this.normalizeDisplayText(source.name),
      unit: this.normalizeDisplayText(source.unit)
    };
  }

  private normalizeCapabilityProof(
    value: unknown
  ): DataEntityVolumeCertificateCapabilityProof | null {
    const source = this.asRecord(value);
    if (!source) {
      return null;
    }

    return {
      tolerance: this.normalizeCertificateFiniteNumber(source.tolerance),
      limit: this.normalizeCertificateFiniteNumber(source.limit),
      index: this.normalizeCertificateFiniteNumber(source.index),
      acceptance: this.normalizeAcceptance(source.acceptance)
    };
  }

  private normalizeComplianceAssessment(
    value: unknown
  ): DataEntityVolumeCertificateComplianceAssessment | null {
    const source = this.asRecord(value);
    if (!source) {
      return null;
    }

    return {
      decisionRule: this.normalizeCertificateFiniteNumber(source.decisionRule),
      enableLowerLimit: this.normalizeAcceptance(source.enableLowerLimit),
      lowerLimit: this.normalizeCertificateFiniteNumber(source.lowerLimit),
      enableUpperLimit: this.normalizeAcceptance(source.enableUpperLimit),
      upperLimit: this.normalizeCertificateFiniteNumber(source.upperLimit),
      acceptanceLowerLimit: this.normalizeCertificateFiniteNumber(source.acceptanceLowerLimit),
      acceptanceUpperLimit: this.normalizeCertificateFiniteNumber(source.acceptanceUpperLimit),
      rejectionLowerLimit: this.normalizeCertificateFiniteNumber(source.rejectionLowerLimit),
      rejectionUpperLimit: this.normalizeCertificateFiniteNumber(source.rejectionUpperLimit),
      value: this.normalizeCertificateFiniteNumber(source.value),
      valueLowerLimit: this.normalizeCertificateFiniteNumber(source.valueLowerLimit),
      valueUpperLimit: this.normalizeCertificateFiniteNumber(source.valueUpperLimit),
      insideProbability: this.normalizeCertificateFiniteNumber(source.insideProbability),
      outsideProbability: this.normalizeCertificateFiniteNumber(source.outsideProbability),
      acceptance: this.normalizeAcceptance(source.acceptance)
    };
  }

  private normalizeCertificateDistribution(
    value: unknown
  ): DataEntityVolumeCertificateDistribution | null {
    const source = this.asRecord(value);
    if (!source) {
      return null;
    }

    return {
      $type: this.normalizeDisplayText(source.$type),
      coverageFactor: this.normalizeCertificateFiniteNumber(source.coverageFactor),
      shapeFactor: this.normalizeCertificateFiniteNumber(source.shapeFactor)
    };
  }

  private normalizeResultDistribution(value: unknown): {
    coverageFactor: number | null;
    shapeFactor: number | null;
  } | null {
    const source = this.asRecord(value);
    if (!source) {
      return null;
    }

    return {
      coverageFactor: this.normalizeCertificateFiniteNumber(source.coverageFactor),
      shapeFactor: this.normalizeCertificateFiniteNumber(source.shapeFactor)
    };
  }

  private normalizeCertificateParameters(
    value: unknown
  ): DataEntityVolumeCertificateParameters | null {
    if (!Array.isArray(value)) {
      return [];
    }

    return value
      .map(item => this.normalizeCertificateParameter(item))
      .filter((item): item is DataEntityVolumeCertificateParameters[number] => item !== null);
  }

  private normalizeCertificateConstants(
    value: unknown
  ): DataEntityVolumeCertificateConstants | null {
    if (!Array.isArray(value)) {
      return [];
    }

    return value
      .map(item => this.normalizeCertificateConstant(item))
      .filter((item): item is DataEntityVolumeCertificateConstants[number] => item !== null);
  }

  private normalizeCertificateRegression(
    value: unknown
  ): DataEntityVolumeCertificateRegression | null {
    const source = this.asRecord(value);
    if (!source) {
      return null;
    }

    return {
      quantities: this.normalizeStringArray(source.quantities),
      expandedUncertainies: this.normalizeStringArray(source.expandedUncertainies),
      slope: this.normalizeCertificateFiniteNumber(source.slope),
      intercept: this.normalizeCertificateFiniteNumber(source.intercept),
      equation: this.normalizeFormulaExpression(source.equation),
      correlation: this.normalizeCertificateFiniteNumber(source.correlation),
      uncertaintyEquation: this.normalizeFormulaExpression(source.uncertaintyEquation),
      bPrime: this.normalizeCertificateFiniteNumber(source.bPrime)
    };
  }

  private normalizeCertificateMonteCarloSimulation(
    value: unknown
  ): DataEntityVolumeCertificateMonteCarloSimulation | null {
    const source = this.asRecord(value);
    if (!source) {
      return null;
    }

    return {
      enabled: this.normalizeAcceptance(source.enabled),
      numSamples: this.normalizeCertificateFiniteNumber(source.numSamples),
      value: this.normalizeCertificateFiniteNumber(source.value),
      standardDeviation: this.normalizeCertificateFiniteNumber(source.standardDeviation),
      standardUncertainy: this.normalizeCertificateFiniteNumber(source.standardUncertainy),
      expandedUncertainy: this.normalizeCertificateFiniteNumber(source.expandedUncertainy),
      coverageMin: this.normalizeCertificateFiniteNumber(source.coverageMin),
      coverageMax: this.normalizeCertificateFiniteNumber(source.coverageMax),
      coverageFactor: this.normalizeCertificateFiniteNumber(source.coverageFactor),
      capabilityLimit: this.normalizeCertificateFiniteNumber(source.capabilityLimit),
      capabilityIndex: this.normalizeCertificateFiniteNumber(source.capabilityIndex),
      capabilityAccept: this.normalizeAcceptance(source.capabilityAccept),
      compliancePInside: this.normalizeCertificateFiniteNumber(source.compliancePInside),
      compliancePOutside: this.normalizeCertificateFiniteNumber(source.compliancePOutside),
      complianceAccept: this.normalizeAcceptance(source.complianceAccept),
      minimum: this.normalizeCertificateFiniteNumber(source.minimum),
      maximum: this.normalizeCertificateFiniteNumber(source.maximum),
      median: this.normalizeCertificateFiniteNumber(source.median),
      mean: this.normalizeCertificateFiniteNumber(source.mean),
      values: this.normalizeCertificateNumberArray(source.values),
      frequencies: this.normalizeCertificateNumberArray(source.frequencies)
    };
  }

  private normalizeCertificateParameter(
    value: unknown
  ): DataEntityVolumeCertificateParameters[number] | null {
    const source = this.asRecord(value);
    if (!source) {
      return null;
    }

    return {
      doF: this.normalizeCertificateValue(source.doF),
      value: this.normalizeCertificateValue(source.value),
      standardUncertainty: this.normalizeCertificateValue(source.standardUncertainty),
      sensitivityCoeff: this.normalizeCertificateValue(source.sensitivityCoeff),
      uncertaintyContribution: this.normalizeCertificateValue(source.uncertaintyContribution),
      relativeContribution: this.normalizeCertificateValue(source.relativeContribution),
      distribution: this.normalizeCertificateDistribution(source.distribution),
      name: this.normalizeDisplayText(source.name),
      unit: this.normalizeDisplayText(source.unit)
    };
  }

  private normalizeCertificateConstant(
    value: unknown
  ): DataEntityVolumeCertificateConstants[number] | null {
    const source = this.asRecord(value);
    if (!source) {
      return null;
    }

    return {
      value: this.normalizeCertificateValue(source.value),
      name: this.normalizeDisplayText(source.name),
      unit: this.normalizeDisplayText(source.unit)
    };
  }

  private normalizeDisplayText(value: unknown): string | null {
    if (typeof value !== 'string') {
      return null;
    }

    const normalized = value.trim();
    return normalized.length > 0 ? normalized : null;
  }

  private normalizeFormulaExpression(value: unknown): string | null {
    if (typeof value === 'string') {
      const normalized = value.trim();
      return normalized.length > 0 ? normalized : null;
    }

    if (typeof value === 'number' || typeof value === 'boolean') {
      return String(value);
    }

    return null;
  }

  private normalizeStringArray(value: unknown): string[] {
    if (!Array.isArray(value)) {
      return [];
    }

    return value
      .map(item => this.normalizeFormulaExpression(item))
      .filter((item): item is string => item !== null);
  }

  private normalizeCertificateValue(
    value: unknown
  ): number | 'Infinity' | null {
    if (value === 'Infinity' || value === Number.POSITIVE_INFINITY) {
      return 'Infinity';
    }

    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }

    return null;
  }

  private normalizeCertificateFiniteNumber(value: unknown): number | null {
    return typeof value === 'number' && Number.isFinite(value) ? value : null;
  }

  private normalizeCertificateNumberArray(value: unknown): number[] {
    return Array.isArray(value)
      ? value.filter((item): item is number =>
        typeof item === 'number' && Number.isFinite(item)
      )
      : [];
  }

  private normalizeAcceptance(value: unknown): boolean | null {
    return typeof value === 'boolean' ? value : null;
  }

  private normalizeCertificateFilePath(value: unknown): string | null {
    if (typeof value !== 'string') {
      return null;
    }

    const normalized = value.trim();
    return normalized.length > 0 ? normalized : null;
  }

  private handleLookupError(_error: HttpErrorResponse): void {
    this.certificateRegistrationStatus = 'unknown';
    this.clearCertificateUiState();
    const defaults = this.cloneDefaultData();
    this.initialFormSnapshot = this.cloneFormModel(defaults);
    this.initializeForm(defaults);
    this.alertifyService.ErrorMessage('دریافت اطلاعات با خطا مواجه شد.');
  }

  initializeForm(data: DataEntityVolumeFormModel, preserveNModels = false): void {
    this.formBindings$.next();
    this.preserveRegisteredNModels = preserveNModels;
    this.form = this.buildForm(data, preserveNModels);
    this.expandedM2Rows = data.m2.map(() => true);
    if (!preserveNModels) {
      this.bindMeasurementValuesToNModels();
    }
  }

  buildForm(data: DataEntityVolumeFormModel, preserveNModels = false): FormGroup {
    const requiredText = [Validators.required];
    const finite = [Validators.required, finiteNumberValidator()];
    const nonNegative = [Validators.required, nonNegativeFiniteNumberValidator()];
    const measurements = data.m2 ?? [];
    const existingNModels = data.NModels ?? [];

    return this.formBuilder.group(
      {
        Unit: [data.Unit ?? ''],
        UncertaintyFloat: [
          data.UncertaintyFloat ?? 2,
          [Validators.required, finiteNumberValidator()]
        ],
        m0: this.createValueContainer(data.m0 && data.m0.Value),
        k0: this.createValueContainer(data.k0 && data.k0.Value),
        pb: this.createValueContainer(data.pb && data.pb.Value),
        a0: this.createValueContainer(data.a0 && data.a0.Value),
        a1: this.createValueContainer(data.a1 && data.a1.Value),
        a2: this.createValueContainer(data.a2 && data.a2.Value),
        a3: this.createValueContainer(data.a3 && data.a3.Value),
        a4: this.createValueContainer(data.a4 && data.a4.Value),
        aC: this.createValueContainer(data.aC && data.aC.Value),
        td20: this.createValueContainer(data.td20 && data.td20.Value),
        m2: this.formBuilder.array(measurements.map(item => this.createM2Item(item))),
        mE: this.createLimitUncertaintyGroup(data.mE),
        DmuB: this.createExpandedUncertaintyGroup(data.DmuB),
        DmLB: this.createLimitUncertaintyGroup(data.DmLB),
        DmRB1: this.createLimitUncertaintyGroup(data.DmRB1),
        DmRB2: this.createLimitUncertaintyGroup(data.DmRB2),
        DDmrB1: this.createLimitUncertaintyGroup(data.DDmrB1),
        DDmrB2: this.createLimitUncertaintyGroup(data.DDmrB2, true),
        DmTDB: this.createLimitUncertaintyGroup(data.DmTDB),
        Pa_IND: this.createLimitUncertaintyGroup(data.Pa_IND),
        Qa_IND: this.createLimitUncertaintyGroup(data.Qa_IND),
        ta_IND: this.createLimitUncertaintyGroup(data.ta_IND),
        tW: this.createLimitUncertaintyGroup(data.tW),
        td: this.createLimitUncertaintyGroup(data.td),
        Description: [data.Description ?? '', requiredText],
        Expert: [data.Expert ?? '', requiredText],
        IssueDateMiladi: [
          data.IssueDateMiladi ?? '',
          [Validators.required]
        ],
        CalibrationDateMiladi: [
          data.CalibrationDateMiladi ?? '',
          [Validators.required]
        ],
        RecalibrationDateMiladi: [
          data.RecalibrationDateMiladi ?? '',
          [Validators.required]
        ],
        Manufacturer: [data.Manufacturer ?? '', requiredText],
        ModelDevice: [data.ModelDevice ?? '', requiredText],
        TypeDevice: [data.TypeDevice ?? '', requiredText],
        CodeNoDevice: [data.CodeNoDevice ?? '', requiredText],
        SerialNoDevice: [data.SerialNoDevice ?? '', requiredText],
        CustomerName: [data.CustomerName ?? '', requiredText],
        CertificateAddress: [data.CertificateAddress ?? '', requiredText],
        LabAdderss: [data.LabAdderss ?? '', requiredText],
        Farsi: [data.Farsi ?? false],
        Value: [data.Value ?? 0, finite],
        From: [data.From ?? 0, finite],
        To: [data.To ?? 0, finite],
        Division: [data.Division ?? 1, [Validators.required, positiveFiniteNumberValidator()]],
        LetterId: [data.LetterId ?? 0, nonNegative],
        CalibrationDetailId: [data.CalibrationDetailId ?? 0, nonNegative],
        NModels: preserveNModels
          ? this.formBuilder.array(existingNModels.map(item => this.createNModelItem(item)))
          : this.buildSynchronizedNModels(measurements, existingNModels)
      },
      {
        validators: [
          orderedNumberValidator('From', 'To', 'fromAfterTo'),
          dateOrderValidator()
        ]
      }
    );
  }

  createValueContainer(value?: number): FormGroup {
    return this.formBuilder.group({
      Value: [
        value ?? 0,
        [Validators.required, finiteNumberValidator()]
      ]
    });
  }

  createObservation(value?: number): FormControl {
    return this.formBuilder.control(value ?? 0, [
      Validators.required,
      finiteNumberValidator()
    ]);
  }

  createM2Item(item: Partial<MeasurementModel> = {}): FormGroup {
    return this.formBuilder.group({
      UncertaintyEvaluation: [
        item.UncertaintyEvaluation ?? TypeAUncertaintyEvaluations.Experimental,
        [
          Validators.required,
          enumValueValidator([
            TypeAUncertaintyEvaluations.Experimental,
            TypeAUncertaintyEvaluations.PooledEstimate
          ])
        ]
      ],
      StandardDeviation: [
        item.StandardDeviation ?? 0,
        [Validators.required, nonNegativeFiniteNumberValidator()]
      ],
      Observations: this.formBuilder.array(
        (item.Observations ?? []).map(value => this.createObservation(value))
      ),
      IndirectObservations: this.formBuilder.array(
        (item.IndirectObservations ?? []).map(value => this.createObservation(value))
      ),
      ObservationMethod: [
        item.ObservationMethod ?? ObservationMethod.Direct,
        [
          Validators.required,
          enumValueValidator([ObservationMethod.Direct, ObservationMethod.Indirect])
        ]
      ],
      UncertaintyEstimate: [
        item.UncertaintyEstimate ?? TypeAUncertaintyEstimates.Standard,
        [
          Validators.required,
          enumValueValidator([
            TypeAUncertaintyEstimates.Standard,
            TypeAUncertaintyEstimates.Bayesian
          ])
        ]
      ],
      DoF: [
        normalizeDoF(item.DoF),
        [Validators.required, finiteNumberOrPositiveInfinityValidator()]
      ],
      Value: [item.Value ?? 0, [Validators.required, finiteNumberValidator()]],
      StandardUncertainty: [
        item.StandardUncertainty ?? 0,
        [Validators.required, nonNegativeFiniteNumberValidator()]
      ],
      SensitivityCoeff: [
        item.SensitivityCoeff ?? 0,
        [Validators.required, finiteNumberValidator()]
      ],
      UncertaintyContribution: [
        item.UncertaintyContribution ?? 0,
        [Validators.required, finiteNumberValidator()]
      ],
      RelativeContribution: [
        item.RelativeContribution ?? 0,
        [Validators.required, finiteNumberValidator()]
      ]
    });
  }

  createNModelItem(item: Partial<NModel> = {}): FormGroup {
    return this.formBuilder.group(
      {
        Value: [item.Value ?? '', Validators.required],
        Tolerance: [
          item.Tolerance ?? 0,
          [Validators.required, nonNegativeFiniteNumberValidator()]
        ],
        TUR: [
          item.TUR ?? 3,
          [Validators.required, nonNegativeFiniteNumberValidator()]
        ],
        LimitValue: [
          item.LimitValue ?? 3,
          [Validators.required, nonNegativeFiniteNumberValidator()]
        ],
        Limit: [
          item.Limit ?? 0,
          [Validators.required, nonNegativeFiniteNumberValidator()]
        ],
        LowerLimit: [
          item.LowerLimit ?? 0,
          [Validators.required, finiteNumberValidator()]
        ],
        UpperLimit: [
          item.UpperLimit ?? 0,
          [Validators.required, finiteNumberValidator()]
        ],
        DecisionRule: [
          item.DecisionRule ?? 0,
          [Validators.required, nonNegativeFiniteNumberValidator()]
        ],
        Distribution: this.createDistributionGroup(item.Distribution)
      },
      { validators: orderedNumberValidator('LowerLimit', 'UpperLimit', 'limitsOrder') }
    );
  }

  private buildSynchronizedNModels(
    measurements: MeasurementModel[],
    existingModels: NModel[]
  ): FormArray {
    return this.formBuilder.array(
      measurements.map((measurement, index) =>
        this.createNModelItem({
          ...(existingModels[index] ?? {}),
          Value: String(measurement.Value ?? 0)
        })
      )
    );
  }

  createDistributionGroup(
    distribution: Partial<DistributionBaseDTO> = {}
  ): FormGroup {
    const group = this.formBuilder.group({
      $type: [
        distribution.$type ?? 'Normal',
        [Validators.required, distributionTypeValidator()]
      ],
      CoverageFactor: [
        distribution.CoverageFactor ?? 2,
        [Validators.required, positiveFiniteNumberValidator()]
      ],
      ShapeFactor: [
        distribution.ShapeFactor ?? 1,
        [Validators.required, positiveFiniteNumberValidator()]
      ]
    });

    group
      .get('$type')
      .valueChanges.pipe(
        takeUntil(this.formBindings$),
        takeUntil(this.destroy$)
      )
      .subscribe(() => this.rebuildDistributionForType(group));

    return group;
  }

  private createLimitUncertaintyGroup(
    item: Partial<LimitUncertaintyDTO<'Infinity' | number>> = {},
    allowInfiniteStandardUncertainty = false
  ): FormGroup {
    return this.formBuilder.group({
      Distribution: this.createDistributionGroup(item.Distribution),
      HalfWidthOfLimit: [
        item.HalfWidthOfLimit ?? 0,
        [Validators.required, nonNegativeFiniteNumberValidator()]
      ],
      RelativeErrorOfUncertainty: [
        item.RelativeErrorOfUncertainty ?? 0,
        [Validators.required, nonNegativeFiniteNumberValidator()]
      ],
      DoF: [
        normalizeDoF(item.DoF),
        [Validators.required, finiteNumberOrPositiveInfinityValidator()]
      ],
      Value: [item.Value ?? 0, [Validators.required, finiteNumberValidator()]],
      StandardUncertainty: [
        item.StandardUncertainty ?? 0,
        allowInfiniteStandardUncertainty
          ? [Validators.required, finiteNumberOrPositiveInfinityValidator()]
          : [Validators.required, nonNegativeFiniteNumberValidator()]
      ],
      SensitivityCoeff: [
        item.SensitivityCoeff ?? 0,
        [Validators.required, finiteNumberValidator()]
      ],
      UncertaintyContribution: [
        item.UncertaintyContribution ?? 0,
        [Validators.required, finiteNumberValidator()]
      ],
      RelativeContribution: [
        item.RelativeContribution ?? 0,
        [Validators.required, finiteNumberValidator()]
      ]
    });
  }

  private createExpandedUncertaintyGroup(
    item: Partial<DataEntityVolumeFormModel['DmuB']> = {}
  ): FormGroup {
    return this.formBuilder.group({
      Distribution: this.createDistributionGroup(item.Distribution),
      ExpandedUncertainty: [
        item.ExpandedUncertainty ?? 0,
        [Validators.required, nonNegativeFiniteNumberValidator()]
      ],
      CoverageFactor: [
        item.CoverageFactor ?? 2,
        [Validators.required, positiveFiniteNumberValidator()]
      ],
      DoF: [
        normalizeDoF(item.DoF),
        [Validators.required, finiteNumberOrPositiveInfinityValidator()]
      ],
      Value: [item.Value ?? 0, [Validators.required, finiteNumberValidator()]],
      StandardUncertainty: [
        item.StandardUncertainty ?? 0,
        [Validators.required, nonNegativeFiniteNumberValidator()]
      ],
      SensitivityCoeff: [
        item.SensitivityCoeff ?? 0,
        [Validators.required, finiteNumberValidator()]
      ],
      UncertaintyContribution: [
        item.UncertaintyContribution ?? 0,
        [Validators.required, finiteNumberValidator()]
      ],
      RelativeContribution: [
        item.RelativeContribution ?? 0,
        [Validators.required, finiteNumberValidator()]
      ]
    });
  }

  private rebuildDistributionForType(group: FormGroup): void {
    // No derived DTO source exists in this repository and the supplied JSON has
    // only the three shared properties. Remove unknown controls on type changes.
    Object.keys(group.controls)
      .filter(key => ['$type', 'CoverageFactor', 'ShapeFactor'].indexOf(key) < 0)
      .forEach(key => group.removeControl(key));
  }

  get m2(): FormArray {
    return this.form.get('m2') as FormArray;
  }

  get nModels(): FormArray {
    return this.form.get('NModels') as FormArray;
  }

  getNModelGroup(index: number): FormGroup {
    return this.nModels.at(index) as FormGroup;
  }

  observations(m2Index: number): FormArray {
    return this.m2.at(m2Index).get('Observations') as FormArray;
  }

  indirectObservations(m2Index: number): FormArray {
    return this.m2.at(m2Index).get('IndirectObservations') as FormArray;
  }

  addM2Item(): void {
    const measurement = this.createM2Item();
    this.m2.push(measurement);
    this.nModels.push(
      this.createNModelItem({
        Value: String(measurement.get('Value').value ?? '')
      })
    );
    this.expandedM2Rows.push(true);
    this.synchronizeNModelValues();
  }

  removeM2Item(index: number): void {
    if (index < 0 || index >= this.m2.length) {
      return;
    }

    this.m2.removeAt(index);
    if (index < this.nModels.length) {
      this.nModels.removeAt(index);
    }
    this.expandedM2Rows.splice(index, 1);
    this.synchronizeNModelStructure();
    this.synchronizeNModelValues();
  }

  addObservation(m2Index: number): void {
    this.observations(m2Index).push(this.createObservation());
  }

  removeObservation(m2Index: number, observationIndex: number): void {
    this.observations(m2Index).removeAt(observationIndex);
  }

  addIndirectObservation(m2Index: number): void {
    this.indirectObservations(m2Index).push(this.createObservation());
  }

  removeIndirectObservation(m2Index: number, observationIndex: number): void {
    this.indirectObservations(m2Index).removeAt(observationIndex);
  }

  resetToDefault(): void {
    this.initializeForm(
      this.cloneFormModel(this.initialFormSnapshot),
      this.certificateRegistrationStatus === 'registered'
    );
  }

  serializeFormValue(): DataEntityVolumeSaveRequest {
    if (!this.preserveRegisteredNModels) {
      this.synchronizeNModelStructure();
      this.synchronizeNModelValues();
    }

    const request = this.clone(
      this.form.getRawValue() as DataEntityVolumeFormModel
    ) as DataEntityVolumeSaveRequest;

    this.valueContainerFields.forEach(field => {
      request[field.key].Value = this.toFiniteNumber(request[field.key].Value);
    });

    request.Value = this.toFiniteNumber(request.Value);
    request.UncertaintyFloat = this.toFiniteNumber(request.UncertaintyFloat);
    request.From = this.toFiniteNumber(request.From);
    request.To = this.toFiniteNumber(request.To);
    request.Division = this.toFiniteNumber(request.Division);
    request.LetterId = this.toFiniteNumber(request.LetterId);
    request.CalibrationDetailId = this.toFiniteNumber(request.CalibrationDetailId);

    request.m2.forEach(item => {
      item.UncertaintyEvaluation = Number(
        item.UncertaintyEvaluation
      ) as TypeAUncertaintyEvaluations;
      item.ObservationMethod = Number(item.ObservationMethod) as ObservationMethod;
      item.UncertaintyEstimate = Number(
        item.UncertaintyEstimate
      ) as TypeAUncertaintyEstimates;
      item.DoF = normalizeDoF(item.DoF);
      item.StandardDeviation = this.toFiniteNumber(item.StandardDeviation);
      item.Observations = item.Observations.map(value => this.toFiniteNumber(value));
      item.IndirectObservations = item.IndirectObservations.map(value =>
        this.toFiniteNumber(value)
      );
      item.Value = this.toFiniteNumber(item.Value);
      item.StandardUncertainty = this.toFiniteNumber(item.StandardUncertainty);
      item.SensitivityCoeff = this.toFiniteNumber(item.SensitivityCoeff);
      item.UncertaintyContribution = this.toFiniteNumber(
        item.UncertaintyContribution
      );
      item.RelativeContribution = this.toFiniteNumber(item.RelativeContribution);
    });

    this.uncertaintySections.forEach(section => {
      const item = request[section.key];
      item.DoF = normalizeDoF(item.DoF);
      item.Value = this.toFiniteNumber(item.Value);
      item.StandardUncertainty = section.allowInfiniteStandardUncertainty
        ? normalizeDoF(item.StandardUncertainty)
        : this.toFiniteNumber(item.StandardUncertainty);
      item.SensitivityCoeff = this.toFiniteNumber(item.SensitivityCoeff);
      item.UncertaintyContribution = this.toFiniteNumber(
        item.UncertaintyContribution
      );
      item.RelativeContribution = this.toFiniteNumber(item.RelativeContribution);
      item.Distribution.CoverageFactor = this.toFiniteNumber(
        item.Distribution.CoverageFactor
      );
      item.Distribution.ShapeFactor = this.toFiniteNumber(
        item.Distribution.ShapeFactor
      );

      if (section.kind === 'limit') {
        item.HalfWidthOfLimit = this.toFiniteNumber(item.HalfWidthOfLimit);
        item.RelativeErrorOfUncertainty = this.toFiniteNumber(
          item.RelativeErrorOfUncertainty
        );
      } else {
        item.ExpandedUncertainty = this.toFiniteNumber(item.ExpandedUncertainty);
        item.CoverageFactor = this.toFiniteNumber(item.CoverageFactor);
      }
    });

    request.NModels.forEach((item, index) => {
      if (!this.preserveRegisteredNModels && request.m2[index]) {
        item.Value = String(request.m2[index].Value);
      }
      item.Tolerance = this.toFiniteNumber(item.Tolerance);
      item.TUR = this.toFiniteNumber(item.TUR);
      item.LimitValue = this.toFiniteNumber(item.LimitValue);
      item.Limit = this.toFiniteNumber(item.Limit);
      item.LowerLimit = this.toFiniteNumber(item.LowerLimit);
      item.UpperLimit = this.toFiniteNumber(item.UpperLimit);
      item.DecisionRule = this.toFiniteNumber(item.DecisionRule);
      item.Distribution.CoverageFactor = this.toFiniteNumber(
        item.Distribution.CoverageFactor
      );
      item.Distribution.ShapeFactor = this.toFiniteNumber(
        item.Distribution.ShapeFactor
      );
    });

    return request;
  }

  private bindMeasurementValuesToNModels(): void {
    this.m2.valueChanges
      .pipe(
        takeUntil(this.formBindings$),
        takeUntil(this.destroy$)
      )
      .subscribe(() => this.synchronizeNModelValues());

    this.synchronizeNModelStructure();
    this.synchronizeNModelValues();
  }

  private synchronizeNModelStructure(): void {
    while (this.nModels.length > this.m2.length) {
      this.nModels.removeAt(this.nModels.length - 1);
    }

    while (this.nModels.length < this.m2.length) {
      const index = this.nModels.length;
      const measurementValue = this.m2.at(index).get('Value').value;
      this.nModels.push(
        this.createNModelItem({
          Value: String(measurementValue ?? '')
        })
      );
    }
  }

  private synchronizeNModelValues(): void {
    this.m2.controls.forEach((measurementControl, index) => {
      const nModelControl = this.nModels.at(index);
      if (!nModelControl) {
        return;
      }

      const measurementValue = measurementControl.get('Value').value;
      nModelControl
        .get('Value')
        .setValue(String(measurementValue ?? ''), { emitEvent: false });
    });
  }

  submitForm(): void {
    if (this.isSubmitting) {
      return;
    }

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.focusFirstInvalidControl();
      this.alertifyService.ErrorMessage('Please correct the validation errors.');
      return;
    }

    const request = this.serializeFormValue();
    request.NModels.forEach((item, index) => {
      const measurement: any = request.m2[index];
      const value = measurement && (measurement.Value ?? measurement.value);
      if (value !== undefined) {
        item.Value = String(value);
      }
    });
    this.isSubmitting = true;

    this.dataEntityVolumeFormService
      .save(request)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isSubmitting = false;
        })
      )
      .subscribe({
        next: rawResponse => {
          const response = this.unwrapDataEntityVolumeResponse(rawResponse);
          this.lastSaveResponse = response;
          this.handleDataEntityVolumeResponse(response, 'post');
          this.alertifyService.SuccessMessage('اطلاعات ثبت شد');
        },
        error: (error: HttpErrorResponse) => this.handleSaveError(error)
      });
  }

  downloadCertificateFile(): void {
    this.downloadDocument(this.certificateFilePath, 'certificate');
  }

  downloadUncertaintyReportFile(): void {
    this.downloadDocument(this.uncertaintyReportFilePath, 'uncertaintyReport');
  }

  private downloadDocument(sourcePath: string | null, type: 'certificate' | 'uncertaintyReport'): void {
    let path = sourcePath;
    const isDownloading = type === 'certificate'
      ? this.isDownloadingCertificate
      : this.isDownloadingUncertaintyReport;
    if (!path || isDownloading) {
      return;
    }

    this.setDocumentDownloading(type, true);

    this.dataEntityVolumeFormService
      .downloadCertificateFile(path)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.setDocumentDownloading(type, false);
        })
      )
      .subscribe({
        next: blob => {
          if (!this.isValidCertificateBlob(blob, path)) {
            this.alertifyService.ErrorMessage(
              'فایل گواهی معتبر دریافت نشد.'
            );
            return;
          }

          this.saveCertificateBlob(blob, path);
        },
        error: (error: HttpErrorResponse) =>
          this.handleCertificateDownloadError(error)
      });
  }

  private setDocumentDownloading(type: 'certificate' | 'uncertaintyReport', value: boolean): void {
    if (type === 'certificate') {
      this.isDownloadingCertificate = value;
    } else {
      this.isDownloadingUncertaintyReport = value;
    }
  }

  private isValidCertificateBlob(blob: Blob, path: string): boolean {
    if (!blob || blob.size <= 0) {
      return false;
    }

    const mimeType = (blob.type || '').toLowerCase();
    if (mimeType.indexOf('text/html') >= 0) {
      return false;
    }

    const isDocxPath = this.getCertificateFileName(path)
      .toLowerCase()
      .endsWith('.docx');
    return (
      mimeType ===
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
      ((mimeType === 'application/octet-stream' || mimeType === '') &&
        isDocxPath)
    );
  }

  private saveCertificateBlob(blob: Blob, path: string): void {
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = this.getCertificateFileName(path);
    anchor.style.display = 'none';

    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(objectUrl);
  }

  private getCertificateFileName(path: string): string {
    const rawFileName = path
      .split('?')[0]
      .split('#')[0]
      .split('/')
      .pop();

    if (rawFileName && /^[^\\/:*?"<>|]+\.docx$/i.test(rawFileName)) {
      return rawFileName;
    }

    return 'certificate.docx';
  }

  private handleCertificateDownloadError(error: HttpErrorResponse): void {
    const message =
      error && error.status === 404
        ? 'فایل گواهی در سرور یافت نشد.'
        : 'دریافت فایل گواهی با خطا مواجه شد.';
    this.alertifyService.ErrorMessage(message);
  }

  applyBackendValidationErrors(errors: Record<string, string[] | string>): void {
    Object.keys(errors).forEach(path => {
      const control = this.findControlByServerPath(path);
      if (!control) {
        return;
      }

      const value = errors[path];
      const message = Array.isArray(value) ? value.join(' ') : value;
      control.setErrors({ ...(control.errors || {}), server: message });
      control.markAsTouched();
    });
  }

  private handleSaveError(error: HttpErrorResponse): void {
    const problem = this.asValidationProblem(error.error);
    if (problem && problem.errors) {
      this.applyBackendValidationErrors(problem.errors);
      this.focusFirstInvalidControl();
    }

    const message =
      (problem && (problem.detail || problem.title)) ||
      (error.status === 0
        ? 'The server is unavailable. Please check your connection.'
        : 'امکان ثبت اطلاعات وجود ندارد');
    this.alertifyService.ErrorMessage(message);
  }

  private asValidationProblem(value: unknown): AspNetValidationProblemDetails | null {
    if (!value || typeof value !== 'object') {
      return null;
    }
    return value as AspNetValidationProblemDetails;
  }

  private findControlByServerPath(path: string): AbstractControl | null {
    const normalizedPath = path
      .replace(/^\$\./, '')
      .replace(/\[(\d+)\]/g, '.$1');
    return this.form.get(normalizedPath);
  }

  private focusFirstInvalidControl(): void {
    setTimeout(() => {
      const element = this.hostElement.nativeElement.querySelector(
        '.ng-invalid[formControlName]'
      ) as HTMLElement;
      if (element) {
        element.focus();
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    });
  }

  onInfinityToggle(control: AbstractControl, event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    control.setValue(checked ? 'Infinity' : 0);
    control.markAsDirty();
    control.markAsTouched();
  }

  isPositiveInfinity(control: AbstractControl): boolean {
    return control && control.value === 'Infinity';
  }

  control(path: string): AbstractControl {
    return this.form.get(path);
  }

  isObservationMethod(m2Index: number, method: ObservationMethod): boolean {
    return Number(this.m2.at(m2Index).get('ObservationMethod').value) === method;
  }

  formatNumericValue(value: number | string | null | undefined): string {
    if (value === 'Infinity' || value === Number.POSITIVE_INFINITY) {
      return '∞';
    }

    if (value === null || value === undefined) {
      return '—';
    }

    if (typeof value === 'number' && !Number.isFinite(value)) {
      return '—';
    }

    return String(value);
  }

  getAcceptanceLabel(value: boolean | null | undefined): string {
    if (value === true) {
      return 'قابل قبول';
    }

    if (value === false) {
      return 'غیرقابل قبول';
    }

    return 'محاسبه نشده';
  }

  getAcceptanceClass(value: boolean | null | undefined): string {
    if (value === true) {
      return 'certificate-status--accepted';
    }

    if (value === false) {
      return 'certificate-status--rejected';
    }

    return 'certificate-status--unknown';
  }

  hasTechnicalSection(value: unknown): boolean {
    return this.toDisplayEntries(value).length > 0;
  }

  toDisplayEntries(value: unknown): Array<{ key: string; value: unknown }> {
    const record = this.asRecord(value);
    if (!record) {
      return [];
    }

    return Object.keys(record).map(key => ({ key, value: record[key] }));
  }

  formatTechnicalValue(value: unknown): string {
    if (value === null || value === undefined) {
      return '—';
    }

    if (typeof value === 'boolean') {
      return this.getAcceptanceLabel(value);
    }

    if (typeof value === 'number' || typeof value === 'string') {
      return this.formatNumericValue(value);
    }

    if (Array.isArray(value)) {
      return value.length
        ? value.map(item => this.formatTechnicalValue(item)).join('، ')
        : '—';
    }

    return this.asRecord(value) ? 'دارای جزئیات' : String(value);
  }

  validationMessage(control: AbstractControl): string {
    if (!control || !control.errors || !control.touched) {
      return '';
    }
    if (control.errors.server) {
      return control.errors.server;
    }
    if (control.errors.required) {
      return 'This field is required.';
    }
    if (control.errors.positive) {
      return 'Enter a number greater than zero.';
    }
    if (control.errors.nonNegative || control.errors.finiteNumberOrInfinity) {
      return 'Enter zero, a positive finite number, or select Positive Infinity.';
    }
    if (control.errors.enum || control.errors.distributionType) {
      return 'Select a valid option.';
    }
    if (control.errors.dateString) {
      return 'تاریخ را با قالب معتبر مانند 2026-01-24T00:00:00 وارد کنید.';
    }
    return 'Enter a valid finite number.';
  }

  trackByIndex(index: number): number {
    return index;
  }

  private normalizeValueContainer(
    value: unknown,
    fallback: DataEntityVolumeFormModel['m0']
  ): DataEntityVolumeFormModel['m0'] {
    const source = this.asRecord(value) || {};
    return {
      Value: this.normalizeFiniteNumber(source.Value, fallback.Value)
    };
  }

  private normalizeLoadedLimit(
    value: unknown,
    fallback: LimitUncertaintyDTO,
    allowInfiniteStandardUncertainty?: false
  ): LimitUncertaintyDTO;
  private normalizeLoadedLimit(
    value: unknown,
    fallback: LimitUncertaintyDTO<number | 'Infinity'>,
    allowInfiniteStandardUncertainty: true
  ): LimitUncertaintyDTO<number | 'Infinity'>;
  private normalizeLoadedLimit(
    value: unknown,
    fallback: LimitUncertaintyDTO<number | 'Infinity'>,
    allowInfiniteStandardUncertainty = false
  ): LimitUncertaintyDTO<number | 'Infinity'> {
    const source = this.asRecord(value) || {};
    return {
      Distribution: this.normalizeDistribution(
        source.Distribution,
        fallback.Distribution
      ),
      HalfWidthOfLimit: this.normalizeNonNegativeNumber(
        source.HalfWidthOfLimit,
        fallback.HalfWidthOfLimit
      ),
      RelativeErrorOfUncertainty: this.normalizeNonNegativeNumber(
        source.RelativeErrorOfUncertainty,
        fallback.RelativeErrorOfUncertainty
      ),
      DoF: this.normalizePositiveInfinity(source.DoF, fallback.DoF),
      Value: this.normalizeFiniteNumber(source.Value, fallback.Value),
      StandardUncertainty: allowInfiniteStandardUncertainty
        ? this.normalizePositiveInfinity(
          source.StandardUncertainty,
          fallback.StandardUncertainty
        )
        : this.normalizeNonNegativeNumber(
          source.StandardUncertainty,
          typeof fallback.StandardUncertainty === 'number'
            ? fallback.StandardUncertainty
            : 0
        ),
      SensitivityCoeff: this.normalizeFiniteNumber(
        source.SensitivityCoeff,
        fallback.SensitivityCoeff
      ),
      UncertaintyContribution: this.normalizeFiniteNumber(
        source.UncertaintyContribution,
        fallback.UncertaintyContribution
      ),
      RelativeContribution: this.normalizeFiniteNumber(
        source.RelativeContribution,
        fallback.RelativeContribution
      )
    };
  }

  private normalizeLoadedExpanded(
    value: unknown,
    fallback: DataEntityVolumeFormModel['DmuB']
  ): DataEntityVolumeFormModel['DmuB'] {
    const source = this.asRecord(value) || {};
    return {
      Distribution: this.normalizeDistribution(
        source.Distribution,
        fallback.Distribution
      ),
      ExpandedUncertainty: this.normalizeNonNegativeNumber(
        source.ExpandedUncertainty,
        fallback.ExpandedUncertainty
      ),
      CoverageFactor: this.normalizePositiveNumber(
        source.CoverageFactor,
        fallback.CoverageFactor
      ),
      DoF: this.normalizePositiveInfinity(source.DoF, fallback.DoF),
      Value: this.normalizeFiniteNumber(source.Value, fallback.Value),
      StandardUncertainty: this.normalizeNonNegativeNumber(
        source.StandardUncertainty,
        fallback.StandardUncertainty
      ),
      SensitivityCoeff: this.normalizeFiniteNumber(
        source.SensitivityCoeff,
        fallback.SensitivityCoeff
      ),
      UncertaintyContribution: this.normalizeFiniteNumber(
        source.UncertaintyContribution,
        fallback.UncertaintyContribution
      ),
      RelativeContribution: this.normalizeFiniteNumber(
        source.RelativeContribution,
        fallback.RelativeContribution
      )
    };
  }

  private normalizeDistribution(
    value: unknown,
    fallback: DistributionBaseDTO
  ): DistributionBaseDTO {
    const source = this.asRecord(value) || {};
    const fallbackType =
      DISTRIBUTION_TYPES.indexOf(fallback.$type) >= 0
        ? fallback.$type
        : ('Normal' as DistributionType);
    const type =
      typeof source.$type === 'string' &&
        DISTRIBUTION_TYPES.indexOf(source.$type as DistributionType) >= 0
        ? (source.$type as DistributionType)
        : fallbackType;

    return {
      $type: type,
      CoverageFactor: this.normalizePositiveNumber(
        source.CoverageFactor,
        this.normalizePositiveNumber(fallback.CoverageFactor, 2)
      ),
      ShapeFactor: this.normalizePositiveNumber(
        source.ShapeFactor,
        this.normalizePositiveNumber(fallback.ShapeFactor, 1)
      )
    };
  }

  private normalizeNumberArray(value: unknown, fallback: number[]): number[] {
    if (!Array.isArray(value)) {
      return this.clone(fallback);
    }

    return value.map((item, index) =>
      this.normalizeFiniteNumber(item, fallback[index] ?? 0)
    );
  }

  private normalizeEnum<T extends number>(
    value: unknown,
    allowedValues: number[],
    fallback: T
  ): T {
    const numericValue =
      typeof value === 'number'
        ? value
        : typeof value === 'string' && /^[+-]?\d+$/.test(value)
          ? Number(value)
          : Number.NaN;
    return allowedValues.indexOf(numericValue) >= 0
      ? (numericValue as T)
      : fallback;
  }

  private normalizePositiveInfinity(
    value: unknown,
    fallback: FiniteNumberOrPositiveInfinity
  ): FiniteNumberOrPositiveInfinity {
    if (value === 'Infinity' || value === Number.POSITIVE_INFINITY) {
      return 'Infinity';
    }
    if (typeof value === 'number' && Number.isFinite(value) && value >= 0) {
      return value;
    }
    return fallback === Number.POSITIVE_INFINITY ? 'Infinity' : fallback;
  }

  private normalizeFiniteNumber(value: unknown, fallback: number): number {
    return typeof value === 'number' && Number.isFinite(value)
      ? value
      : fallback;
  }

  private normalizeNonNegativeNumber(value: unknown, fallback: number): number {
    return typeof value === 'number' && Number.isFinite(value) && value >= 0
      ? value
      : fallback;
  }

  private normalizePositiveNumber(value: unknown, fallback: number): number {
    return typeof value === 'number' && Number.isFinite(value) && value > 0
      ? value
      : fallback;
  }

  private normalizeString(value: unknown, fallback: string): string {
    return typeof value === 'string' ? value : fallback;
  }

  private normalizeBoolean(value: unknown, fallback: boolean): boolean {
    return typeof value === 'boolean' ? value : fallback;
  }

  private asRecord(value: unknown): DataEntityVolumeLookupModel | null {
    return value !== null && typeof value === 'object' && !Array.isArray(value)
      ? (value as DataEntityVolumeLookupModel)
      : null;
  }

  private hasLookupFields(record: DataEntityVolumeLookupModel): boolean {
    const formKeys = Object.keys(
      defaultVolumeData as unknown as Record<string, unknown>
    );
    return formKeys.some(key =>
      Object.prototype.hasOwnProperty.call(record, key)
    );
  }

  private createDefaultMeasurement(): MeasurementModel {
    return {
      UncertaintyEvaluation: TypeAUncertaintyEvaluations.Experimental,
      StandardDeviation: 0,
      Observations: [],
      IndirectObservations: [],
      ObservationMethod: ObservationMethod.Direct,
      UncertaintyEstimate: TypeAUncertaintyEstimates.Standard,
      DoF: 0,
      Value: 0,
      StandardUncertainty: 0,
      SensitivityCoeff: 0,
      UncertaintyContribution: 0,
      RelativeContribution: 0
    };
  }

  private createDefaultNModel(): NModel {
    return {
      Value: '0',
      Tolerance: 0,
      TUR: 3,
      LimitValue: 3,
      Limit: 0,
      LowerLimit: 0,
      UpperLimit: 0,
      DecisionRule: 0,
      Distribution: {
        $type: 'Normal',
        CoverageFactor: 2,
        ShapeFactor: 1
      }
    };
  }

  private cloneDefaultData(): DataEntityVolumeFormModel {
    return this.clone(
      defaultVolumeData as unknown as DataEntityVolumeFormModel
    );
  }

  private cloneFormModel(
    value: DataEntityVolumeFormModel
  ): DataEntityVolumeFormModel {
    return this.clone(value);
  }

  private toFiniteNumber(value: unknown): number {
    const numberValue = Number(value);
    return Number.isFinite(numberValue) ? numberValue : 0;
  }

  private clone<T>(value: T): T {
    return JSON.parse(JSON.stringify(value)) as T;
  }
}
